// Dependency-free relative-date formatter (previously backed by moment-timezone).
//
// Produces a coarse relative label ("Today", "Next Week", "In 3 months", ...)
// for a date, computed in the runtime's local timezone. The group-card date is
// rendered client-side after mount, so this resolves to the viewer's local
// timezone. Dropping moment-timezone here removes it from the home page bundle
// (and every other page that only used moment via this formatter).
//
// The bucketing intentionally mirrors the previous moment-based implementation;
// parity is guarded by src/qa-vitest/calendarFormat.spec.ts.

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:zestlife-web":true});}catch(e){}}();const MS_PER_DAY = 24 * 60 * 60 * 1000

const isSameCalendarDate = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate()

/** now shifted by whole days, keeping local-time semantics. */
const addDays = (d: Date, n: number): Date =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate() + n)

/**
 * Start of the containing week as a timestamp. Weeks start on Sunday to match
 * moment's default `en` locale (dow: 0). Two dates in the same Sun–Sat week
 * share this value regardless of a month/year boundary in between.
 */
const weekStartTime = (d: Date): number =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate() - d.getDay()).getTime()

const isSameWeek = (a: Date, b: Date): boolean =>
  weekStartTime(a) === weekStartTime(b)

/** now shifted by whole months, clamping the day to the target month (Jan 31 + 1mo → Feb 28), like moment.add(). */
const addMonths = (d: Date, n: number): Date => {
  const firstOfTarget = new Date(d.getFullYear(), d.getMonth() + n, 1)
  const daysInTarget = new Date(
    firstOfTarget.getFullYear(),
    firstOfTarget.getMonth() + 1,
    0,
  ).getDate()
  return new Date(
    firstOfTarget.getFullYear(),
    firstOfTarget.getMonth(),
    Math.min(d.getDate(), daysInTarget),
    d.getHours(),
    d.getMinutes(),
    d.getSeconds(),
    d.getMilliseconds(),
  )
}

/**
 * Fractional month difference `a - b`, mirroring moment's internal monthDiff
 * (including the day-of-month normalization on the first line) so rounding at
 * half-month boundaries matches the previous implementation.
 */
const monthDiff = (a: Date, b: Date): number => {
  if (a.getDate() < b.getDate()) {
    return -monthDiff(b, a)
  }
  const wholeMonthDiff =
    (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth())
  const anchor = addMonths(a, wholeMonthDiff)
  const delta = b.getTime() - anchor.getTime()
  let adjust: number
  if (delta < 0) {
    const anchor2 = addMonths(a, wholeMonthDiff - 1)
    adjust = delta / (anchor.getTime() - anchor2.getTime())
  } else {
    const anchor2 = addMonths(a, wholeMonthDiff + 1)
    adjust = delta / (anchor2.getTime() - anchor.getTime())
  }
  return -(wholeMonthDiff + adjust) || 0
}

// Accepts native date inputs plus moment-like values (anything with valueOf()
// returning a timestamp), so existing callers that pass a Moment keep working
// without pulling moment back into this module.
type DateInput = string | number | Date | { valueOf(): number } | null | undefined

const calendarFormat = (dateTime: DateInput): string => {
  if (dateTime == null) {
    return ""
  }

  const target =
    typeof dateTime === "object" && !(dateTime instanceof Date)
      ? new Date(dateTime.valueOf())
      : new Date(dateTime)
  if (Number.isNaN(target.getTime())) {
    return ""
  }
  const now = new Date()

  if (isSameCalendarDate(target, now)) return "Today"
  if (isSameCalendarDate(target, addDays(now, 1))) return "Tomorrow"
  if (isSameCalendarDate(target, addDays(now, -1))) return "Yesterday"

  const sameMonthYear = (a: Date, b: Date) =>
    a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()

  if (isSameWeek(target, now) && sameMonthYear(target, now)) return "This Week"

  const nextWeekRef = addDays(now, 7)
  if (isSameWeek(target, nextWeekRef) && sameMonthYear(target, nextWeekRef))
    return "Next Week"

  const lastWeekRef = addDays(now, -7)
  if (isSameWeek(target, lastWeekRef) && sameMonthYear(target, lastWeekRef))
    return "Last Week"

  if (sameMonthYear(target, now)) return "This Month"
  if (sameMonthYear(target, addMonths(now, 1))) return "Next Month"
  if (sameMonthYear(target, addMonths(now, -1))) return "Past Month"

  if (target.getFullYear() === now.getFullYear() + 1) return "Next Year"
  if (target.getFullYear() === now.getFullYear() - 1) return "Past Year"

  const targetFromNowMonths = monthDiff(target, now)
  // Labels round the reference-minus-target diff, matching the original
  // `now.diff(from, unit)` (negated, since Math.round is asymmetric at .5).
  const roundedYears = Math.abs(Math.round(-targetFromNowMonths / 12))
  const roundedMonths = Math.abs(Math.round(-targetFromNowMonths))

  if (targetFromNowMonths / 12 > 1) return `In ${roundedYears} years`
  if (targetFromNowMonths / 12 < -1) return `${roundedYears} years ago`
  if ((target.getTime() - now.getTime()) / MS_PER_DAY > 0) {
    return `In ${roundedMonths} months`
  }
  return `${roundedMonths} months ago`
}

export default calendarFormat
